import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/page-header";
import { Link, useSearch, useLocation as useWouterLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { AlertTriangle, Settings, Users, Building, Info, RefreshCw, TrendingUp, Globe, PawPrint, CheckCircle2, ChevronRight, Calendar, Play, X, BarChart3, LayoutGrid } from "lucide-react";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { format, parseISO } from "date-fns";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { useCapacitySummary, useCapacityLocation } from "@/hooks/use-capacity";
import { 
  SystemCapacitySummary, 
  LocationCapacityCard, 
  LocationCapacityGrid 
} from "@/components/capacity/capacity-summary";
import { CapacityTrendsPanel } from "@/components/capacity/capacity-trends";
import { PopulationBreakdownPanel } from "@/components/capacity/population-breakdowns";
import { IntakeOutcomeChart } from "@/components/capacity/intake-outcome-chart";
import { OnHoldChart } from "@/components/capacity/on-hold-chart";
import { LengthOfStayChart } from "@/components/capacity/length-of-stay-chart";
import { AverageStaysChart } from "@/components/capacity/average-stays-chart";
import { OutcomeReasonChart } from "@/components/capacity/outcome-reason-chart";
import { SubstatusChart } from "@/components/capacity/substatus-chart";
import { EvaluationBreakdownChart } from "@/components/capacity/evaluation-breakdown-chart";
import { IntakeSourcesChart } from "@/components/capacity/intake-sources-chart";
import { InsightsSectionNav, type SectionDef } from "@/components/capacity/insights-section-nav";
import { HintBlock } from "@/components/ui/hint-block";
import { InsightsPageWrapper } from "@/components/insights/insights-layout";
import { useInsightsContext } from "@/contexts/insights-context";
import type { CapacityStatus, ZoneCapacityData, LocationSummaryData } from "@/hooks/use-capacity";
import { CapacitySummaryBar } from "@/components/capacity/capacity-summary-bar";
import { useSystemSnapshots, useLocationSnapshots, useCapacityScheduleSettings } from "@/hooks/use-capacity-snapshots";
import { ReportActionItems } from "@/components/report-action-items";
import { usePageTitle } from "@/hooks/use-page-title";
import { useReportFreshness } from "@/hooks/use-report-freshness";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";

function getStatusColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive text-destructive-foreground";
    case "near":
      return "bg-amber-500 text-white dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 text-white dark:bg-emerald-600";
    case "unknown":
    default:
      return "bg-muted text-muted-foreground";
  }
}

function getStatusTooltip(status: CapacityStatus): string {
  switch (status) {
    case "ok":
      return "Utilization is within normal operating range";
    case "near":
      return "Approaching capacity limits — monitor closely";
    case "over":
      return "Exceeding configured capacity — immediate attention needed";
    case "unknown":
    default:
      return "Capacity data is not available or not yet configured";
  }
}

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function getStatusLabel(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "Over Capacity";
    case "near":
      return "Near Capacity";
    case "ok":
      return "OK";
    case "unknown":
    default:
      return "No capacity set";
  }
}

function getProgressColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive";
    case "near":
      return "bg-amber-500 dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 dark:bg-emerald-600";
    default:
      return "bg-muted";
  }
}

function formatDataAsOf(dateString: string | null): string {
  if (!dateString) return "Unknown";
  try {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  } catch {
    return "Unknown";
  }
}

function ZoneCard({ zone, locationCode }: { zone: ZoneCapacityData; locationCode: string }) {
  const hasCapacity = zone.capacity !== null;
  const capacity = zone.capacity ?? 0;
  const utilization = hasCapacity && capacity > 0 
    ? Math.round((zone.occupancy / capacity) * 100) 
    : 0;

  // Build link to animals page with location and zone filters
  const animalsUrl = `/animals?location=${encodeURIComponent(locationCode)}&zone=${encodeURIComponent(zone.zoneCode)}`;
  const zoneTestId = zone.zoneCode.replace(/\s+/g, '-');

  return (
    <Card 
      className="group relative overflow-visible transition-shadow"
      data-testid={`card-zone-${zoneTestId}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base font-semibold" data-testid={`text-zone-code-${zoneTestId}`}>
            {zone.zoneCode}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge 
                  className={getStatusColor(zone.status)}
                  data-testid={`badge-status-${zoneTestId}`}
                >
                  {getStatusLabel(zone.status)}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{getStatusTooltip(zone.status)}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <CardDescription className="text-sm">{zone.areaLabel}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {hasCapacity ? (
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div 
                className={`h-full transition-all ${getProgressColor(zone.status)}`}
                style={{ width: `${Math.min(utilization, 100)}%` }}
              />
            </div>
          ) : (
            <div className="h-3 w-full rounded-full border border-dashed border-muted-foreground/30" />
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium" data-testid={`text-occupancy-${zoneTestId}`}>
              {zone.occupancy} {hasCapacity ? `/ ${zone.capacity}` : 'animals'}
            </span>
            {hasCapacity && (
              <span className="text-muted-foreground" data-testid={`text-utilization-${zoneTestId}`}>
                {utilization}%
              </span>
            )}
          </div>
        </div>
      </CardContent>
      
      {/* Hover overlay with View Animals button - matches dashboard location card style */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-all duration-200 flex items-center justify-center"
        data-testid={`overlay-zone-${zoneTestId}`}
      >
        <Button variant="outline" size="sm" asChild>
          <Link href={animalsUrl} data-testid={`button-view-animals-${zoneTestId}`}>
            <Users className="h-4 w-4 mr-2" />
            View Animals
          </Link>
        </Button>
      </div>
    </Card>
  );
}

interface LocationSummaryCardProps {
  summary: { 
    totalAnimals: number; 
    zonesWithCapacity: number; 
    zonesWithoutCapacity: number; 
    dataAsOf: string | null;
    isStale: boolean;
    noReportsWarning: boolean;
  };
  location: string;
  zones: ZoneCapacityData[];
  snapshotsEnabled?: boolean;
  onSwitchToKennels?: () => void;
}

function LocationSummaryCard({ summary, location, zones, snapshotsEnabled, onSwitchToKennels }: LocationSummaryCardProps) {
  // Calculate utilization from configured zones
  const configuredZones = zones.filter(z => z.capacity !== null);
  const totalCapacity = configuredZones.reduce((sum, z) => sum + (z.capacity ?? 0), 0);
  const totalOccupancyConfigured = configuredZones.reduce((sum, z) => sum + z.occupancy, 0);
  const hasUtilization = totalCapacity > 0;
  const utilizationValue = hasUtilization ? Math.round((totalOccupancyConfigured / totalCapacity) * 100) : 0;
  
  // Derive status from utilization
  const status: "over" | "near" | "ok" | "unknown" = 
    !hasUtilization ? "unknown" :
    utilizationValue > 100 ? "over" :
    utilizationValue >= 80 ? "near" : "ok";
  
  const zonesTotal = summary.zonesWithCapacity + summary.zonesWithoutCapacity;

  return (
    <Card className="mb-6" data-testid="card-location-summary">
      <CardContent className="py-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              <span className="text-lg font-semibold" data-testid="text-location-title">
                {location} Overview
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge 
                      className={getStatusColor(status)}
                      data-testid="badge-location-status"
                    >
                      {getStatusLabel(status)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getStatusTooltip(status)}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              {summary.dataAsOf && (
                <Badge 
                  variant="secondary"
                  className="flex items-center gap-1.5"
                  data-testid="text-location-data-as-of"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Data as of {formatFullDate(summary.dataAsOf)}</span>
                </Badge>
              )}
              {snapshotsEnabled && (
                <Badge 
                  className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="badge-snapshots-enabled"
                >
                  <Play className="h-3 w-3" />
                  <span>Snapshots Enabled</span>
                </Badge>
              )}
            </div>
          </div>

          {hasUtilization ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>
                  <strong data-testid="text-location-occupancy">{summary.totalAnimals}</strong> animals
                </span>
                <span className="font-medium" data-testid="text-location-utilization">
                  {utilizationValue}%
                </span>
              </div>
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
                <div 
                  className={`h-full transition-all ${getProgressColor(status)}`}
                  style={{ width: `${Math.min(utilizationValue, 100)}%` }}
                  data-testid="progress-location-utilization"
                />
              </div>
            </div>
          ) : (
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertDescription className="text-amber-700 dark:text-amber-300" data-testid="text-no-utilization">
                {summary.totalAnimals} animals (no capacity configured)
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              <span data-testid="text-location-coverage">
                Utilization based on <strong className="text-foreground">{summary.zonesWithCapacity}</strong> of {zonesTotal} kennels
              </span>
              {onSwitchToKennels && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSwitchToKennels}
                    className="p-0 h-auto text-sm underline underline-offset-2"
                    data-testid="link-view-kennels"
                  >
                    View kennels &rarr;
                  </Button>
                </>
              )}
            </div>
            {summary.noReportsWarning && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-amber-600 dark:text-amber-400" data-testid="text-location-no-reports">
                  No completed reports
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NoReportsWarning({ location }: { location: string }) {
  const [dismissed, setDismissed] = useState(false);
  
  if (dismissed) return null;
  
  return (
    <Alert className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-500 relative">
      <AlertTriangle className="h-4 w-4" />
      <button 
        type="button"
        className="absolute top-3 right-3 p-1 rounded-sm text-amber-600 hover:text-amber-800 hover:bg-amber-200 dark:text-amber-400 dark:hover:text-amber-200 dark:hover:bg-amber-800 transition-colors"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-no-reports-alert"
      >
        <X className="h-4 w-4" />
      </button>
      <AlertTitle className="text-amber-800 dark:text-amber-200 pr-8" data-testid="text-no-reports-warning">
        No recent reports found for {location}
      </AlertTitle>
      <AlertDescription className="text-amber-700 dark:text-amber-300">
        The capacity view shows animals from the past 7 days. Upload a new All Pets report to update this data.
      </AlertDescription>
    </Alert>
  );
}

function StaleDataWarning({ dataAsOf, locationName, onOpenModal }: { dataAsOf: string | null; locationName: string; onOpenModal?: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  
  if (dismissed) return null;
  
  return (
    <Alert className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500 relative">
      <Info className="h-4 w-4" />
      <button 
        type="button"
        className="absolute top-3 right-3 p-1 rounded-sm text-blue-600 hover:text-blue-800 hover:bg-blue-200 dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-800 transition-colors"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-stale-data-alert"
      >
        <X className="h-4 w-4" />
      </button>
      <AlertTitle className="text-blue-800 dark:text-blue-200 pr-8" data-testid="text-stale-data-warning">
        Population data derived from All Pets reports
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-normal text-amber-700 dark:text-amber-400 ml-2 cursor-help inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Stale Data
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium mb-1">Stale locations:</p>
            <ul className="list-disc pl-4">
              <li>{locationName}</li>
            </ul>
          </TooltipContent>
        </Tooltip>
      </AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
        <span>Upload a new All Pets report to see the latest occupancy data.</span>
        {onOpenModal && (
          <Button variant="outline" size="sm" onClick={onOpenModal} className="h-7 bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 ml-auto" data-testid="button-view-report-freshness">
            View Report Status
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function SystemStaleDataWarning({ staleCount, staleLocationNames = [], onOpenModal }: { staleCount: number; staleLocationNames?: string[]; onOpenModal?: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  
  if (staleCount === 0) {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="text-population-data-info">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>Population data derived from All Pets reports</span>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 hover-elevate rounded-sm px-1"
          onClick={onOpenModal}
          data-testid="badge-report-freshness"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>All locations current</span>
        </button>
      </div>
    );
  }
  
  if (dismissed) return null;
  
  return (
    <Alert className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500 relative">
      <Info className="h-4 w-4" />
      <button 
        type="button"
        className="absolute top-3 right-3 p-1 rounded-sm text-blue-600 hover:text-blue-800 hover:bg-blue-200 dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-800 transition-colors"
        onClick={() => setDismissed(true)}
        data-testid="button-dismiss-system-stale-alert"
      >
        <X className="h-4 w-4" />
      </button>
      <AlertTitle className="text-blue-800 dark:text-blue-200 pr-8" data-testid="text-system-stale-warning">
        Population data derived from All Pets reports
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-normal text-amber-700 dark:text-amber-400 ml-2 cursor-help inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Stale Data
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium mb-1">Stale locations:</p>
            {staleLocationNames.length > 0 ? (
              <ul className="list-disc pl-4">
                {staleLocationNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            ) : (
              <p>{staleCount} location{staleCount !== 1 ? "s" : ""}</p>
            )}
          </TooltipContent>
        </Tooltip>
      </AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
        <span>Upload new All Pets reports to see the latest occupancy data.</span>
        {onOpenModal && (
          <Button variant="outline" size="sm" onClick={onOpenModal} className="h-7 bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 ml-auto" data-testid="button-view-report-freshness-system">
            View Report Status
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function ZoneCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-4 w-32 mt-1" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          <Skeleton className="h-3 w-full rounded-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-10" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SystemSummaryContent({ onLocationClick }: { onLocationClick: (locationCode: string) => void }) {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  
  const { 
    data: summaryData, 
    isLoading: summaryLoading, 
    isError: summaryError 
  } = useCapacitySummary();

  const {
    data: snapshotsData,
  } = useSystemSnapshots(30);

  const { data: scheduleSettings } = useCapacityScheduleSettings();

  if (summaryError) {
    return (
      <Card className="mb-6 border-destructive/50 bg-destructive/10">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-destructive">Failed to load capacity summary. Please refresh the page.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (summaryLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="py-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-20" />
            </div>
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="flex gap-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summaryData) return null;

  const latestSnapshot = (() => {
    const snapshots = snapshotsData?.snapshots ?? [];
    if (snapshots.length === 0) return null;
    const sortedSnapshots = [...snapshots].sort((a, b) => b.snapshotDate.localeCompare(a.snapshotDate));
    const snapshotWithData = sortedSnapshots.find(s => s.dataAsOf !== null);
    return snapshotWithData ?? sortedSnapshots[0];
  })();
  
  const snapshotSummaryData = latestSnapshot ? {
    occupancyAllKennels: latestSnapshot.occupancyAllKennels,
    dataAsOf: latestSnapshot.dataAsOf,
    staleLocationCount: summaryData.systemSummary?.staleLocationCount ?? 0,
    currentLocationCount: latestSnapshot.currentLocationCount,
  } : null;

  return (
    <>
      <SystemCapacitySummary 
        data={summaryData.systemSummary} 
        snapshotData={snapshotSummaryData}
        isAdmin={isAdmin}
        snapshotsEnabled={scheduleSettings?.isEnabled}
      />
      
      <section id="section-population-by-location" className="mt-8">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold" data-testid="text-locations-header">
            Population by Location
          </h2>
        </div>
      
        <LocationCapacityGrid>
        {summaryData.locationSummaries.map(loc => (
          <LocationCapacityCard
            key={loc.location}
            data={loc}
            isSelected={false}
            onClick={() => onLocationClick(loc.location)}
          />
        ))}
      </LocationCapacityGrid>
      </section>
    </>
  );
}

function SystemKennelsContent() {
  const { locations: activeLocations } = useActiveLocations();
  const { 
    data: summaryData, 
    isLoading: summaryLoading,
  } = useCapacitySummary();

  if (summaryLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <ZoneCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!summaryData) return null;

  return (
    <div className="space-y-2">
      {summaryData.locationSummaries.map(loc => (
        <SystemLocationKennels 
          key={loc.location} 
          locationCode={loc.location} 
          locationLabel={loc.locationLabel}
          locationSummary={loc}
        />
      ))}
      {summaryData.locationSummaries.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground" data-testid="text-no-locations">
              No locations found.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SystemLocationKennels({ locationCode, locationLabel, locationSummary }: { locationCode: string; locationLabel: string; locationSummary: LocationSummaryData }) {
  const { 
    data: locationData, 
    isLoading: locationLoading, 
  } = useCapacityLocation(locationCode);

  if (locationLoading) {
    return (
      <Card className="mb-4">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2">
            <ChevronRight className="h-4 w-4 shrink-0" />
            <span className="text-base font-semibold" data-testid={`button-toggle-system-kennels-${locationCode}`}>{locationLabel}</span>
            <Skeleton className="h-5 w-16" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (!locationData) return null;

  const zonesWithCapacity = locationData.zones.filter(z => z.capacity !== null);
  const zonesWithoutCapacity = locationData.zones.filter(z => z.capacity === null);
  const totalZones = locationData.zones.length;

  return (
    <CollapsibleKennelSection
      title={locationLabel}
      count={totalZones}
      zones={[...zonesWithCapacity, ...zonesWithoutCapacity]}
      locationCode={locationCode}
      variant="primary"
      testId={`system-kennels-${locationCode}`}
      summaryData={{
        totalAnimals: locationSummary.occupancyAllZones,
        utilizationPercent: locationSummary.utilizationConfigured,
        zonesWithCapacity: locationSummary.zonesWithCapacity,
        zonesTotal: locationSummary.zonesTotal,
        status: locationSummary.status,
      }}
    />
  );
}

const INSIGHT_HINTS: Record<string, string> = {
  "population-breakdowns": "Current population at each location, broken down by kennel status. Use this to quickly identify which locations are near capacity.",
  "capacity-utilization": "Percentage of kennel capacity in use over time (only kennels with configured limits). Look for sustained upward trends that may require proactive transfers or intake holds.",
  "kennel-population": "Total animal count across all kennels over time, regardless of capacity limits. Compare with utilization to understand headroom.",
  "population-breakdown": "Daily composition of the population by species, kennel status, behavior eval, or adoption level. Switch views to spot shifts in the mix over time.",
  "kennel-status-trends": "Distribution of kennel statuses over time — available, unavailable, in evaluation, or on stray wait. A shrinking available area signals tightening capacity.",
  "adoption-pipeline": "Adoption readiness levels (L1-L4) over time. Animals progress from L1 (Easy) to L4 (Expert) based on behavior and medical evaluations. A healthy distribution shows steady progression toward adoption-ready.",
  "intakes-outcomes": "Daily new intakes vs. outcomes over time. The gap between the two lines shows whether the population is growing or shrinking. Helps identify surges, seasonal patterns, and flow. Toggle hold trends to see how holds affect flow.",
  "on-hold": "Animals currently on legal, medical, or behavioral hold, grouped by hold date to show how long holds have been active. High hold counts reduce available capacity even when kennels appear open.",
  "availability-breakdown": "Available animals broken down by behavioral and medical flags — \"No Flags\" means ready for adoption with no special notes. Toggle to Unavailable to see medical, behavioral, combined, and pending adoption substatuses. Helps understand the composition of each group.",
  "evaluation-breakdown": "Animals currently in evaluation, broken down by assessment status: Complete, Needs Review, Needs Recs, Unknown, Error, and No Status. Tracks progress from initial intake through complete evaluation and helps identify backlogs.",
  "length-of-stay": "How long current and recently outcomed animals have been in the shelter, grouped by species. Longer stays may indicate adoption barriers and strain resources. Note: \"Currently In Shelter\" always reflects a live snapshot of animals still in care and does not change with the date range selector.",
  "average-stays": "Average number of separate shelter stays per animal. Higher values may indicate returns or repeated intake cycles.",
  "outcome-reasons": "Where animals went when they left the shelter — adoptions, transfers, returns to owner, and other dispositions. Tracks the mix of outcomes over time.",
  "intake-sources": "Top organizations and sources that animals come from. Shows unique animals per source in the selected period. Useful for evaluating transfer partnerships and intake planning.",
};


function SystemInsightsContent({ days, showHints }: { days: number; showHints: boolean }) {
  const { 
    data: summaryData, 
  } = useCapacitySummary();

  return (
    <>
      {summaryData && (
        <section className="mb-8 space-y-4">
          <CapacityTrendsPanel
            locations={summaryData.locationSummaries.map((l) => ({
              code: l.location,
              label: l.locationLabel,
            }))}
            syncWithLocation={null}
            controlledDays={days}
            hideDateControls
            renderHint={(key) => <HintBlock show={showHints} testId={`hint-${key}`}>{INSIGHT_HINTS[key]}</HintBlock>}
          />
        </section>
      )}

      <section id="section-intakes-outcomes" className="mb-8">
        <HintBlock show={showHints} testId="hint-intakes-outcomes">{INSIGHT_HINTS["intakes-outcomes"]}</HintBlock>
        <IntakeOutcomeChart days={days} />
      </section>

      <section id="section-length-of-stay" className="mb-8">
        <HintBlock show={showHints} testId="hint-length-of-stay">{INSIGHT_HINTS["length-of-stay"]}</HintBlock>
        <LengthOfStayChart days={days} />
      </section>

      <section id="section-average-stays" className="mb-8">
        <HintBlock show={showHints} testId="hint-average-stays">{INSIGHT_HINTS["average-stays"]}</HintBlock>
        <AverageStaysChart days={days} />
      </section>

      <section id="section-outcome-reasons" className="mb-8">
        <HintBlock show={showHints} testId="hint-outcome-reasons">{INSIGHT_HINTS["outcome-reasons"]}</HintBlock>
        <OutcomeReasonChart days={days} />
      </section>

      <section id="section-intake-sources" className="mb-8">
        <HintBlock show={showHints} testId="hint-intake-sources">{INSIGHT_HINTS["intake-sources"]}</HintBlock>
        <IntakeSourcesChart days={days} />
      </section>
    </>
  );
}

function CollapsibleKennelSection({ 
  title, 
  count, 
  zones, 
  locationCode, 
  variant,
  testId,
  defaultOpen = false,
  summaryData,
}: { 
  title: string; 
  count: number; 
  zones: ZoneCapacityData[]; 
  locationCode: string;
  variant: "primary" | "other";
  testId: string;
  defaultOpen?: boolean;
  summaryData?: {
    totalAnimals: number;
    utilizationPercent: number | null;
    zonesWithCapacity: number;
    zonesTotal: number;
    status: CapacityStatus;
  };
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="mb-4">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none py-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <span className={`text-base font-semibold ${variant === "other" ? "text-muted-foreground" : ""}`} data-testid={`button-toggle-${testId}`}>
                  {title}
                </span>
                <Badge variant={variant === "primary" ? "secondary" : "outline"} data-testid={`badge-${testId}`}>
                  {count} {count === 1 ? "Kennel" : "Kennels"}
                </Badge>
                <span className="ml-auto">
                  <ExpandCollapseButton
                    expanded={isOpen}
                    pointerEvents={false}
                    tabIndex={-1}
                  />
                </span>
              </div>
              {summaryData && (
                <div className="pl-6" onClick={(e) => e.stopPropagation()}>
                  <CapacitySummaryBar
                    totalAnimals={summaryData.totalAnimals}
                    utilizationPercent={summaryData.utilizationPercent}
                    zonesWithCapacity={summaryData.zonesWithCapacity}
                    zonesTotal={summaryData.zonesTotal}
                    status={summaryData.status}
                    testIdPrefix={`capacity-${testId}`}
                  />
                </div>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {summaryData && <hr className="mx-4 border-border" />}
          <CardContent className="px-4 pb-4 pt-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {zones.map((zone) => (
                <ZoneCard key={zone.zoneCode} zone={zone} locationCode={locationCode} />
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function useLocationLabelLookup(locationCode: string) {
  const { locations: activeLocations } = useActiveLocations();
  const { data: summaryData } = useCapacitySummary();
  
  return (code: string): string => {
    if (activeLocations.length > 0) {
      const loc = activeLocations.find((l) => l.code === code);
      if (loc) return loc.label;
    }
    const locFromSummary = summaryData?.locationSummaries.find(l => l.location === code);
    if (locFromSummary) return locFromSummary.locationLabel;
    return code;
  };
}

function LocationSummaryTabContent({ locationCode, onSwitchToKennels }: { locationCode: string; onSwitchToKennels: () => void }) {
  const getLocationLabel = useLocationLabelLookup(locationCode);
  const { data: scheduleSettings } = useCapacityScheduleSettings();
  
  const { 
    data: locationData, 
    isLoading: locationLoading, 
    isError: locationError,
    refetch: refetchLocation 
  } = useCapacityLocation(locationCode);

  const { isLoading: summaryLoading } = useCapacitySummary();
  const isLoading = summaryLoading || locationLoading;

  if (locationError) {
    return (
      <>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold" data-testid="text-selected-location-header">
            {getLocationLabel(locationCode)} Summary
          </h2>
          <Button variant="outline" size="sm" onClick={() => refetchLocation()} data-testid="button-retry">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
        <Card className="mb-6 border-destructive/50 bg-destructive/10">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-destructive">Failed to load location summary. Please try again.</p>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!locationData) return null;

  return (
    <LocationSummaryCard 
      summary={locationData.summary} 
      location={getLocationLabel(locationCode)} 
      zones={locationData.zones} 
      snapshotsEnabled={scheduleSettings?.isEnabled}
      onSwitchToKennels={onSwitchToKennels}
    />
  );
}

function LocationKennelsTabContent({ locationCode }: { locationCode: string }) {
  const getLocationLabel = useLocationLabelLookup(locationCode);
  
  const { 
    data: locationData, 
    isLoading: locationLoading, 
    isError: locationError,
    refetch: refetchLocation 
  } = useCapacityLocation(locationCode);

  if (locationError) {
    return (
      <>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold" data-testid="text-selected-location-header">
            {getLocationLabel(locationCode)} Kennel Overview
          </h2>
          <Button variant="outline" size="sm" onClick={() => refetchLocation()} data-testid="button-retry">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
        <Card className="mb-6 border-destructive/50 bg-destructive/10">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-destructive">Failed to load kennel details. Please try again.</p>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  if (locationLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <ZoneCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!locationData) return null;

  const zonesWithCapacity = locationData.zones.filter(z => z.capacity !== null);
  const zonesWithoutCapacity = locationData.zones.filter(z => z.capacity === null);

  const allZones = locationData.zones;
  const totalAnimals = allZones.reduce((sum, z) => sum + z.occupancy, 0);
  const configuredCapacity = zonesWithCapacity.reduce((sum, z) => sum + (z.capacity ?? 0), 0);
  const configuredOccupancy = zonesWithCapacity.reduce((sum, z) => sum + z.occupancy, 0);
  const configuredUtilization = configuredCapacity > 0 ? Math.round((configuredOccupancy / configuredCapacity) * 100) : null;
  const configuredStatus: CapacityStatus = configuredUtilization === null ? "unknown" : configuredUtilization > 100 ? "over" : configuredUtilization >= 80 ? "near" : "ok";

  const unconfiguredOccupancy = zonesWithoutCapacity.reduce((sum, z) => sum + z.occupancy, 0);

  return (
    <>
      {zonesWithCapacity.length > 0 && (
        <CollapsibleKennelSection
          title={`${getLocationLabel(locationCode)} Kennels`}
          count={zonesWithCapacity.length}
          zones={zonesWithCapacity}
          locationCode={locationCode}
          variant="primary"
          testId="kennels-configured"
          defaultOpen={true}
          summaryData={{
            totalAnimals: totalAnimals,
            utilizationPercent: configuredUtilization,
            zonesWithCapacity: zonesWithCapacity.length,
            zonesTotal: allZones.length,
            status: configuredStatus,
          }}
        />
      )}

      {zonesWithoutCapacity.length > 0 && (
        <CollapsibleKennelSection
          title="Other Kennels"
          count={zonesWithoutCapacity.length}
          zones={zonesWithoutCapacity}
          locationCode={locationCode}
          variant="other"
          testId="kennels-other"
          defaultOpen={true}
          summaryData={{
            totalAnimals: unconfiguredOccupancy,
            utilizationPercent: null,
            zonesWithCapacity: 0,
            zonesTotal: zonesWithoutCapacity.length,
            status: "unknown" as CapacityStatus,
          }}
        />
      )}

      {locationData.zones.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground" data-testid="text-no-kennels">
              No animals found for {getLocationLabel(locationCode)} within the staleness window.
            </p>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function LocationInsightsTabContent({ locationCode, days, showHints }: { locationCode: string; days: number; showHints: boolean }) {
  const { data: summaryData } = useCapacitySummary();

  return (
    <>
      {summaryData && (
        <section className="mb-8 space-y-4">
          <CapacityTrendsPanel
            locations={summaryData.locationSummaries.map((l) => ({
              code: l.location,
              label: l.locationLabel,
            }))}
            syncWithLocation={locationCode}
            controlledDays={days}
            hideDateControls
            renderHint={(key) => <HintBlock show={showHints} testId={`hint-${key}`}>{INSIGHT_HINTS[key]}</HintBlock>}
          />
        </section>
      )}

      <section id="section-intakes-outcomes" className="mb-8">
        <HintBlock show={showHints} testId="hint-intakes-outcomes">{INSIGHT_HINTS["intakes-outcomes"]}</HintBlock>
        <IntakeOutcomeChart days={days} />
      </section>

      <section id="section-length-of-stay" className="mb-8">
        <HintBlock show={showHints} testId="hint-length-of-stay">{INSIGHT_HINTS["length-of-stay"]}</HintBlock>
        <LengthOfStayChart days={days} />
      </section>

      <section id="section-average-stays" className="mb-8">
        <HintBlock show={showHints} testId="hint-average-stays">{INSIGHT_HINTS["average-stays"]}</HintBlock>
        <AverageStaysChart days={days} />
      </section>

      <section id="section-outcome-reasons" className="mb-8">
        <HintBlock show={showHints} testId="hint-outcome-reasons">{INSIGHT_HINTS["outcome-reasons"]}</HintBlock>
        <OutcomeReasonChart days={days} />
      </section>

      <section id="section-intake-sources" className="mb-8">
        <HintBlock show={showHints} testId="hint-intake-sources">{INSIGHT_HINTS["intake-sources"]}</HintBlock>
        <IntakeSourcesChart days={days} />
      </section>
    </>
  );
}

const OVERVIEW_SNAPSHOT_DAYS = 90;

const OVERVIEW_SECTIONS_SYSTEM: SectionDef[] = [
  { id: "section-population-by-location", label: "Population by Location", group: "Snapshot" },
  { id: "section-population-breakdowns", label: "Population Breakdown", group: "Snapshot" },
  { id: "section-availability-breakdown", label: "Availability Breakdown", group: "Snapshot" },
  { id: "section-evaluation-breakdown", label: "Evaluation Breakdown", group: "Snapshot" },
  { id: "section-on-hold", label: "On Hold", group: "Snapshot" },
];

const OVERVIEW_SECTIONS_LOCATION: SectionDef[] = [
  { id: "section-population-breakdowns", label: "Population Breakdown", group: "Snapshot" },
  { id: "section-availability-breakdown", label: "Availability Breakdown", group: "Snapshot" },
  { id: "section-evaluation-breakdown", label: "Evaluation Breakdown", group: "Snapshot" },
  { id: "section-on-hold", label: "On Hold", group: "Snapshot" },
];

function OverviewChartSections({ snapshots, snapshotsLoading, snapshotsError, refetchSnapshots, showHints }: {
  snapshots: any[];
  snapshotsLoading: boolean;
  snapshotsError: boolean;
  refetchSnapshots: () => void;
  showHints: boolean;
}) {
  return (
    <div className="mt-8 space-y-8">
      <section id="section-population-breakdowns" className="mb-8">
        <HintBlock show={showHints} testId="hint-population-breakdowns">{INSIGHT_HINTS["population-breakdowns"]}</HintBlock>
        <PopulationBreakdownPanel
          snapshots={snapshots}
          isLoading={snapshotsLoading}
          isError={snapshotsError}
          onRetry={refetchSnapshots}
        />
      </section>

      <section id="section-availability-breakdown" className="mb-8">
        <HintBlock show={showHints} testId="hint-availability-breakdown">{INSIGHT_HINTS["availability-breakdown"]}</HintBlock>
        <SubstatusChart days={OVERVIEW_SNAPSHOT_DAYS} />
      </section>

      <section id="section-evaluation-breakdown" className="mb-8">
        <HintBlock show={showHints} testId="hint-evaluation-breakdown">{INSIGHT_HINTS["evaluation-breakdown"]}</HintBlock>
        <EvaluationBreakdownChart days={OVERVIEW_SNAPSHOT_DAYS} />
      </section>

      <section id="section-on-hold" className="mb-8">
        <HintBlock show={showHints} testId="hint-on-hold">{INSIGHT_HINTS["on-hold"]}</HintBlock>
        <OnHoldChart />
      </section>
    </div>
  );
}

function SystemOverviewCharts({ showHints }: { showHints: boolean }) {
  const {
    data: snapshotsData,
    isLoading: snapshotsLoading,
    isError: snapshotsError,
    refetch: refetchSnapshots,
  } = useSystemSnapshots(OVERVIEW_SNAPSHOT_DAYS);

  return (
    <OverviewChartSections
      snapshots={snapshotsData?.snapshots ?? []}
      snapshotsLoading={snapshotsLoading}
      snapshotsError={snapshotsError}
      refetchSnapshots={() => refetchSnapshots()}
      showHints={showHints}
    />
  );
}

function LocationOverviewCharts({ locationCode, showHints }: { locationCode: string; showHints: boolean }) {
  const {
    data: snapshotsData,
    isLoading: snapshotsLoading,
    isError: snapshotsError,
    refetch: refetchSnapshots,
  } = useLocationSnapshots(locationCode, OVERVIEW_SNAPSHOT_DAYS);

  return (
    <OverviewChartSections
      snapshots={snapshotsData?.snapshots ?? []}
      snapshotsLoading={snapshotsLoading}
      snapshotsError={snapshotsError}
      refetchSnapshots={() => refetchSnapshots()}
      showHints={showHints}
    />
  );
}

function SystemOverviewContent({ onLocationClick, showHints }: { onLocationClick: (locationCode: string) => void; showHints: boolean }) {
  return (
    <>
      <SystemSummaryContent onLocationClick={onLocationClick} />
      <SystemOverviewCharts showHints={showHints} />
    </>
  );
}

function LocationOverviewTabContent({ locationCode, onSwitchToKennels, showHints }: { locationCode: string; onSwitchToKennels: () => void; showHints: boolean }) {
  return (
    <>
      <LocationSummaryTabContent locationCode={locationCode} onSwitchToKennels={onSwitchToKennels} />
      <LocationOverviewCharts locationCode={locationCode} showHints={showHints} />
    </>
  );
}

type PopulationTab = "overview" | "kennels" | "insights";

const VALID_TABS: PopulationTab[] = ["overview", "kennels", "insights"];

function parseTabParam(param: string | null): PopulationTab {
  if (param === "summary") return "overview";
  if (param && VALID_TABS.includes(param as PopulationTab)) {
    return param as PopulationTab;
  }
  return "overview";
}

const HINTS_STORAGE_KEY = "beti-insights-show-hints";

function InsightsTabWrapper({ isSystemView, locationCode }: { isSystemView: boolean; locationCode: string }) {
  const [insightsDays, setInsightsDays] = useState(90);
  const [showHints, setShowHints] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HINTS_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleShowHintsChange = useCallback((checked: boolean) => {
    setShowHints(checked);
    try {
      localStorage.setItem(HINTS_STORAGE_KEY, String(checked));
    } catch {}
  }, []);

  return (
    <div>
      <InsightsSectionNav
        days={insightsDays}
        onDaysChange={setInsightsDays}
        showHints={showHints}
        onShowHintsChange={handleShowHintsChange}
      />
      {isSystemView ? (
        <SystemInsightsContent days={insightsDays} showHints={showHints} />
      ) : (
        <LocationInsightsTabContent locationCode={locationCode} days={insightsDays} showHints={showHints} />
      )}
    </div>
  );
}

function OverviewTabWrapper({ isSystemView, locationCode, onLocationClick, onSwitchToKennels }: {
  isSystemView: boolean;
  locationCode: string;
  onLocationClick: (locationCode: string) => void;
  onSwitchToKennels: () => void;
}) {
  const [showHints, setShowHints] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HINTS_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleShowHintsChange = useCallback((checked: boolean) => {
    setShowHints(checked);
    try {
      localStorage.setItem(HINTS_STORAGE_KEY, String(checked));
    } catch {}
  }, []);

  return (
    <div>
      <InsightsSectionNav
        sections={isSystemView ? OVERVIEW_SECTIONS_SYSTEM : OVERVIEW_SECTIONS_LOCATION}
        showDateRange={false}
        dateLabel=""
        showHints={showHints}
        onShowHintsChange={handleShowHintsChange}
      />
      {isSystemView ? (
        <SystemOverviewContent onLocationClick={onLocationClick} showHints={showHints} />
      ) : (
        <LocationOverviewTabContent locationCode={locationCode} onSwitchToKennels={onSwitchToKennels} showHints={showHints} />
      )}
    </div>
  );
}

export default function PopulationInsights() {
  usePageTitle("Population");
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const { selectedShelter, setSelectedShelter, isSystemView } = useInsightsContext();
  const { locations } = useActiveLocations();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const [, setWouterLocation] = useWouterLocation();

  const [activeTab, setActiveTab] = useState<PopulationTab>(() => {
    return parseTabParam(searchParams.get("tab"));
  });

  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const newSearch = params.toString();
    const newPath = `/population${newSearch ? `?${newSearch}` : ''}`;
    const currentPath = window.location.pathname + window.location.search;
    if (newPath === currentPath) {
      return;
    }
    setWouterLocation(newPath, { replace: true });
  }, [setWouterLocation]);

  useEffect(() => {
    updateUrlParams({
      tab: activeTab === "overview" ? null : activeTab,
    });
  }, [activeTab, updateUrlParams]);

  const [initialHash] = useState(() => window.location.hash);

  useEffect(() => {
    if (!initialHash) return;
    const targetId = initialHash.slice(1);
    let attempts = 0;
    const maxAttempts = 20;
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        attempts++;
        if (attempts < maxAttempts) setTimeout(tryScroll, 250);
        return;
      }
      const container = document.querySelector("[data-insights-scroll-container]") as HTMLElement | null;
      if (container) {
        const nav = container.querySelector("[data-insights-section-nav]") as HTMLElement | null;
        const navHeight = nav?.offsetHeight ?? 48;
        const stickyOffset = navHeight + 16;
        const targetRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTarget = container.scrollTop + (targetRect.top - containerRect.top) - stickyOffset;
        container.scrollTo({ top: Math.max(0, scrollTarget), behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const timer = setTimeout(tryScroll, 500);
    return () => clearTimeout(timer);
  }, [initialHash]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as PopulationTab);
  }, []);

  const handleLocationCardClick = useCallback((locationCode: string) => {
    setSelectedShelter(locationCode);
  }, [setSelectedShelter]);

  const handleSwitchToKennels = useCallback(() => {
    setActiveTab("kennels");
  }, []);

  const { data: locationData } = useCapacityLocation(isSystemView ? "" : selectedShelter);
  const kennelCount = locationData?.zones?.length ?? 0;

  const { data: summaryData } = useCapacitySummary();
  const getLocationLabel = useLocationLabelLookup(selectedShelter);

  const systemStaleCount = summaryData?.systemSummary?.staleLocationCount ?? 0;
  const systemStaleNames = summaryData?.locationSummaries
    .filter(l => l.isStale)
    .map(l => l.locationLabel) ?? [];

  const [freshnessModalOpen, setFreshnessModalOpen] = useState(false);
  const { allPets: allPetsFreshness, interaction: interactionFreshness } = useReportFreshness();

  return (
    <InsightsPageWrapper>
      <PageHeader
        breadcrumb={[
          { label: "Monitoring" },
          { label: "Population" }
        ]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-population">
            <BarChart3 className="w-6 h-6 text-primary" />
            Population
          </span>
        }
        hideBreadcrumbOnMobile
        actions={null}
      />

      <ReportFreshnessModal
        open={freshnessModalOpen}
        onOpenChange={setFreshnessModalOpen}
        staleLocations={[
          ...allPetsFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "allPets" as const })),
          ...interactionFreshness.staleLocations.map(l => ({ ...l, sourceReportType: "interaction" as const })),
        ]}
        locationsWithGaps={[
          ...allPetsFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "allPets" as const })),
          ...interactionFreshness.locationsWithGaps.map(l => ({ ...l, sourceReportType: "interaction" as const })),
        ]}
        reportTypes={["allPets", "interaction"]}
        allPetsLocations={allPetsFreshness.locations}
        interactionLocations={interactionFreshness.locations}
      />

      {isSystemView ? (
        <SystemStaleDataWarning staleCount={systemStaleCount} staleLocationNames={systemStaleNames} onOpenModal={() => setFreshnessModalOpen(true)} />
      ) : (
        <>
          {locationData?.summary.isStale && (
            <StaleDataWarning dataAsOf={locationData.summary.dataAsOf} locationName={getLocationLabel(selectedShelter)} onOpenModal={() => setFreshnessModalOpen(true)} />
          )}
          {locationData?.summary.noReportsWarning && (
            <NoReportsWarning location={getLocationLabel(selectedShelter)} />
          )}
        </>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-wrap items-center justify-between gap-2">
        <TabsList className="h-11 p-1" data-testid="tabs-population">
          <TabsTrigger
            value="overview"
            className="h-9 px-4 text-sm font-medium gap-2"
            data-testid="tab-population-overview"
          >
            <Globe className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="kennels"
            className="h-9 px-4 text-sm font-medium gap-2"
            data-testid="tab-population-kennels"
          >
            <LayoutGrid className="h-4 w-4" />
            Kennels
            {!isSystemView && kennelCount > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs" data-testid="badge-kennel-count">
                {kennelCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="insights"
            className="h-9 px-4 text-sm font-medium gap-2"
            data-testid="tab-population-insights"
          >
            <BarChart3 className="h-4 w-4" />
            Insights
          </TabsTrigger>
        </TabsList>
          <div className="flex flex-wrap items-center gap-2" data-testid="population-action-bar">
            <ReportActionItems
              currentLocation={selectedShelter}
              defaultReportType="all_pets"
              showOtherType={true}
            />
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid="link-manage-kennels"
              >
                <Link href="/admin/settings?tab=zones">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Manage Kennels</span>
                </Link>
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="overview" className="mt-4" data-testid="content-overview">
          <OverviewTabWrapper
            isSystemView={isSystemView}
            locationCode={selectedShelter}
            onLocationClick={handleLocationCardClick}
            onSwitchToKennels={handleSwitchToKennels}
          />
        </TabsContent>

        <TabsContent value="kennels" className="mt-4" data-testid="content-kennels">
          {isSystemView ? (
            <SystemKennelsContent />
          ) : (
            <LocationKennelsTabContent locationCode={selectedShelter} />
          )}
        </TabsContent>

        <TabsContent value="insights" className="mt-4" data-testid="content-insights">
          <InsightsTabWrapper
            isSystemView={isSystemView}
            locationCode={selectedShelter}
          />
        </TabsContent>
      </Tabs>
    </InsightsPageWrapper>
  );
}
